/**
 * Sharing utilities - create shares, get shared plans
 */
import * as authUtils from './auth';
import { BACKEND_URL } from './backendUrl';

/**
 * Create a shareable link for a plan
 */
export const createShareLink = async (planId, permissions = {}) => {
  // Check authentication first
  if (!authUtils.isAuthenticated()) {
    throw new Error('Not authenticated');
  }

  const token = authUtils.getAccessToken();
  if (!token || !token.trim()) {
    throw new Error('Not authenticated');
  }

  const authHeader = authUtils.getAuthHeader();
  if (!authHeader.Authorization || !authHeader.Authorization.trim()) {
    throw new Error('Not authenticated');
  }

  const defaultPermissions = {
    can_view: true,
    can_edit_shopping_list: false,
    can_edit_theme: false,
    ...permissions,
  };

  const response = await fetch(`${BACKEND_URL}/api/plans/${planId}/share/link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
    },
    body: JSON.stringify({
      share_type: 'link',
      permissions: defaultPermissions,
    }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      authUtils.clearAuth();
      throw new Error('Not authenticated');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create share link');
  }

  const share = await response.json();
  return share;
};

/**
 * Create an email invite
 */
export const createEmailInvite = async (planId, email, permissions = {}) => {
  const defaultPermissions = {
    can_view: true,
    can_edit_shopping_list: true,
    can_edit_theme: false,
    ...permissions,
  };

  const response = await fetch(`${BACKEND_URL}/api/plans/${planId}/share/email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authUtils.getAuthHeader(),
    },
    body: JSON.stringify({
      share_type: 'email',
      invited_email: email,
      permissions: defaultPermissions,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send invite');
  }

  return response.json();
};

/**
 * Get shared plan by token (no auth required)
 */
export const getSharedPlan = async (shareToken) => {
  const response = await fetch(`${BACKEND_URL}/api/share/${shareToken}`);

  if (!response.ok) {
    throw new Error('Failed to load shared plan');
  }

  return response.json();
};

/**
 * Get share permissions
 */
export const getSharePermissions = async (shareToken) => {
  const response = await fetch(`${BACKEND_URL}/api/share/${shareToken}/permissions`);

  if (!response.ok) {
    throw new Error('Failed to get share permissions');
  }

  return response.json();
};

/**
 * List all shares for a plan
 */
export const listPlanShares = async (planId) => {
  // Check authentication first
  if (!authUtils.isAuthenticated()) {
    throw new Error('Not authenticated');
  }

  const token = authUtils.getAccessToken();
  if (!token || !token.trim()) {
    throw new Error('Not authenticated');
  }

  const authHeader = authUtils.getAuthHeader();
  if (!authHeader.Authorization || !authHeader.Authorization.trim()) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${BACKEND_URL}/api/plans/${planId}/shares`, {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      authUtils.clearAuth();
      throw new Error('Not authenticated');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to list shares');
  }

  return response.json();
};

/**
 * Revoke a share
 */
export const revokeShare = async (shareToken) => {
  const response = await fetch(`${BACKEND_URL}/api/share/${shareToken}`, {
    method: 'DELETE',
    headers: {
      ...authUtils.getAuthHeader(),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to revoke share');
  }

  return response.json();
};

/**
 * Generate share URL
 */
export const getShareUrl = (shareToken) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/share/${shareToken}`;
};

